/**
 * Study Mode Hero component - Claude Code style hero section
 */

import {useState, useEffect} from 'react';
import {Link} from 'react-router-dom';
import {Container} from '../ui/Container';
import {Button} from '../ui/Button';
import {ROUTES} from '../../config/constants';
import {useTypewriter} from '../../hooks/useTypewriter';

export function StudyHero() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const typedWord = useTypewriter({
    words: ['students', 'professors', 'teachers', 'researchers', 'learners'],
    typingSpeed: 100,
    pauseTime: 2500,
  });

  return (
    <section className="relative flex items-center justify-center pt-32 pb-20 md:pb-24 bg-background dark:bg-[#1E1E1E] overflow-hidden">
      <Container className="relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Status Badge */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-backgroundSecondary dark:bg-[#252526] border border-gray-200 dark:border-gray-700 mb-8 transition-all duration-700 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
          }`}>
            <div className="w-2 h-2 rounded-full bg-orange-500"></div>
            <span className="text-sm text-textSecondary dark:text-[#858585]">Optimizing your study workflow...</span>
          </div>

          {/* Main Headline - Claude Code Style */}
          <div className={`space-y-6 mb-12 transition-all duration-700 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{transitionDelay: isLoaded ? '100ms' : '0ms'}}
          >
            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-heading font-bold leading-tight tracking-tight">
              <span className="text-textPrimary dark:text-[#CCCCCC]">Built for</span>
              {' '}
              <span className="text-orange-600 dark:text-orange-500">&gt;</span>
              {' '}
              <span className="text-orange-600 dark:text-orange-500 inline-block min-w-[200px] sm:min-w-[300px] lg:min-w-[400px] text-left">
                {typedWord}
                <span className="animate-pulse">|</span>
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl lg:text-2xl text-textSecondary dark:text-[#858585] leading-relaxed max-w-3xl mx-auto">
              Unleash AI's raw power directly in your study workflow. Transform messy notes into structured knowledge instantly. Turn hours of studying into focused learning sessions. Your materials. Your pace. Your understanding, evolving at thought speed.
            </p>
          </div>

          {/* CTAs */}
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{transitionDelay: isLoaded ? '200ms' : '0ms'}}
          >
            <Link to={ROUTES.STUDY_APP}>
              <Button variant="primary" size="lg" className="w-full sm:w-auto bg-[#1E1E1E] dark:bg-[#1E1E1E] text-white hover:bg-[#2D2D30] dark:hover:bg-[#2D2D30]">
                Get Started Free
              </Button>
            </Link>
            <Link to={ROUTES.STUDY_APP}>
              <Button variant="outline" size="lg" className="w-full sm:w-auto bg-backgroundSecondary dark:bg-[#252526] border-gray-300 dark:border-gray-600 hover:bg-background dark:hover:bg-[#1E1E1E]">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </Container>
    </section>
  );
}

